import {
  useState,
  useEffect,
  useCallback,
  useRef,
  createContext,
  useContext,
  createElement,
  type ReactNode,
} from 'react';
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { setSentryUser, clearSentryUser } from '@/lib/sentry';
// Session timeout configuration
const SESSION_CONFIG = {
  REFRESH_THRESHOLD_MINUTES: 5,
  CHECK_INTERVAL_MS: 60 * 1000,
  WARN_BEFORE_EXPIRY_MINUTES: 2,
  IDLE_TIMEOUT_MS: 30 * 60 * 1000,
  ACTIVITY_EVENTS: ['mousedown', 'keydown', 'scroll', 'touchstart'] as const,
};

export interface SessionStatus {
  isExpiringSoon: boolean;
  minutesUntilExpiry: number | null;
  isIdle: boolean;
  lastActivityAt: number;
}

interface UseAuthResult {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isRecoveryMode: boolean;
  authEvent: AuthChangeEvent | null;
  sessionStatus: SessionStatus;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: unknown }>;
  signIn: (email: string, password: string) => Promise<{ data: unknown; error: unknown }>;
  signOut: () => Promise<{ error: unknown }>;
  resetPassword: (email: string) => Promise<{ error: unknown }>;
  updatePassword: (newPassword: string) => Promise<{ error: unknown }>;
  clearRecoveryMode: () => void;
  extendSession: () => Promise<boolean>;
  refreshSession: () => Promise<boolean>;
}

const AuthContext = createContext<UseAuthResult | null>(null);

function useAuthState(): UseAuthResult {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  // Check URL for recovery indicators immediately to avoid race condition
  // Also check localStorage in case the page reloaded during recovery
  const [isRecoveryMode, setIsRecoveryMode] = useState(() => {
    const hash = window.location.hash;
    const search = window.location.search;
    const storedRecovery = localStorage.getItem('passwordRecoveryMode');
    // Check both hash (Supabase tokens), query param (our custom flag), and localStorage
    const isRecovery =
      hash.includes('type=recovery') ||
      search.includes('mode=recovery') ||
      storedRecovery === 'true';
    // If recovery detected, persist to localStorage
    if (isRecovery) {
      localStorage.setItem('passwordRecoveryMode', 'true');
    }
    return isRecovery;
  });
  const [authEvent, setAuthEvent] = useState<AuthChangeEvent | null>(null);
  // Session timeout state
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>({
    isExpiringSoon: false,
    minutesUntilExpiry: null,
    isIdle: false,
    lastActivityAt: Date.now(),
  });

  // Refs for timeout tracking
  const lastActivityRef = useRef<number>(Date.now());
  const sessionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Ref for signOut to avoid circular dependency in checkSessionStatus
  const signOutRef = useRef<() => Promise<{ error: unknown }>>(() =>
    Promise.resolve({ error: null }),
  );

  // Reset activity timer on user interaction
  // IMPORTANT: Don't trigger state updates on every activity event (mousedown, etc.)
  // as this causes re-renders that interrupt navigation clicks.
  // Only update state when idle status actually CHANGES.
  const resetActivityTimer = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;

    // Only update state if we were previously idle (to clear idle status)
    // This prevents re-renders on every click/keypress
    setSessionStatus((prev) => {
      if (prev.isIdle) {
        // Was idle, now active — restart Supabase auto-refresh so token stays alive
        supabase.auth.startAutoRefresh();
        return { ...prev, isIdle: false, lastActivityAt: now };
      }
      // Already active - don't trigger re-render, just return same object
      return prev;
    });

    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
    }
    if (SESSION_CONFIG.IDLE_TIMEOUT_MS > 0) {
      idleTimeoutRef.current = setTimeout(() => {
        // Stop Supabase auto-refresh so the token can actually expire when idle
        supabase.auth.stopAutoRefresh();
        setSessionStatus((prev) => ({ ...prev, isIdle: true }));
      }, SESSION_CONFIG.IDLE_TIMEOUT_MS);
    }
  }, []);

  // Refresh session before expiry
  const refreshSession = useCallback(async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        console.error('[Auth] Session refresh failed:', error.message);
        return false;
      }
      if (data.session) {
        setSession(data.session);
        setUser(data.session.user);
        return true;
      }
      return false;
    } catch (err) {
      console.error('[Auth] Session refresh error:', err);
      return false;
    }
  }, []);

  // Check session status and refresh if needed
  const checkSessionStatus = useCallback(async () => {
    if (!session?.expires_at) {
      setSessionStatus((prev) => ({
        ...prev,
        isExpiringSoon: false,
        minutesUntilExpiry: null,
      }));
      return;
    }
    const expiresAt = session.expires_at * 1000;
    const now = Date.now();
    const msUntilExpiry = expiresAt - now;
    const minutesUntilExpiry = Math.max(0, Math.floor(msUntilExpiry / 60000));

    // If token has fully expired, sign out immediately
    if (msUntilExpiry <= 0) {
      console.log('[Auth] Session expired, signing out');
      await signOutRef.current();
      return;
    }

    // Only auto-refresh if user is actively using the app (not idle).
    // When idle, let the token expire naturally so the timeout flow works.
    const isCurrentlyIdle = now - lastActivityRef.current >= SESSION_CONFIG.IDLE_TIMEOUT_MS;
    if (!isCurrentlyIdle && minutesUntilExpiry <= SESSION_CONFIG.REFRESH_THRESHOLD_MINUTES) {
      console.log('[Auth] Session expiring soon, refreshing...');
      const refreshed = await refreshSession();
      if (refreshed) {
        setSessionStatus((prev) => ({
          ...prev,
          isExpiringSoon: false,
          minutesUntilExpiry: null,
        }));
        return;
      }
    }

    const isExpiringSoon =
      SESSION_CONFIG.WARN_BEFORE_EXPIRY_MINUTES > 0 &&
      minutesUntilExpiry <= SESSION_CONFIG.WARN_BEFORE_EXPIRY_MINUTES;
    setSessionStatus((prev) => ({
      ...prev,
      isExpiringSoon,
      minutesUntilExpiry,
    }));
  }, [session, refreshSession]);

  // Set up activity listeners and session check interval
  useEffect(() => {
    const handleActivity = () => resetActivityTimer();
    SESSION_CONFIG.ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    resetActivityTimer();
    if (session) {
      sessionCheckIntervalRef.current = setInterval(
        checkSessionStatus,
        SESSION_CONFIG.CHECK_INTERVAL_MS,
      );
      checkSessionStatus();
    }
    return () => {
      SESSION_CONFIG.ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
      }
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
    };
  }, [session, resetActivityTimer, checkSessionStatus]);

  useEffect(() => {
    // Set up auth state listener FIRST
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setAuthEvent(event);
      // Detect password recovery flow
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecoveryMode(true);
        localStorage.setItem('passwordRecoveryMode', 'true');
      }
      setSession(session);
      setUser(session?.user ?? null);

      // Update Sentry user context
      if (session?.user) {
        setSentryUser({
          id: session.user.id,
          email: session.user.email,
          username: session.user.user_metadata?.full_name,
        });
      } else {
        clearSentryUser();
      }

      setLoading(false);
    });

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      // Update Sentry user context for existing session
      if (session?.user) {
        setSentryUser({
          id: session.user.id,
          email: session.user.email,
          username: session.user.user_metadata?.full_name,
        });
      }

      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: `${window.location.origin}/`, data: { full_name: fullName } },
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (!error) resetActivityTimer();
    return { data, error };
  };

  const signOut = async () => {
    clearSentryUser();
    setSessionStatus({
      isExpiringSoon: false,
      minutesUntilExpiry: null,
      isIdle: false,
      lastActivityAt: Date.now(),
    });
    const { error } = await supabase.auth.signOut({ scope: 'global' });
    Object.keys(localStorage)
      .filter(
        (k) =>
          k.startsWith('sb-') ||
          k.includes('supabase') ||
          k === 'pending2FA' ||
          k === 'passwordRecoveryMode',
      )
      .forEach((k) => localStorage.removeItem(k));
    return { error };
  };

  // Keep ref in sync so checkSessionStatus can call signOut without circular deps
  signOutRef.current = signOut;

  const resetPassword = async (email: string) => {
    // Include mode=recovery query param so we can detect recovery on redirect
    const redirectUrl = `${window.location.origin}/auth?mode=recovery`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (!error) {
      setIsRecoveryMode(false);
      localStorage.removeItem('passwordRecoveryMode');
    }
    return { error };
  };

  // Extend session manually (useful for user action before timeout)
  const extendSession = useCallback(async () => {
    resetActivityTimer();
    return refreshSession();
  }, [resetActivityTimer, refreshSession]);

  const clearRecoveryMode = () => {
    setIsRecoveryMode(false);
    localStorage.removeItem('passwordRecoveryMode');
  };

  return {
    user,
    session,
    loading,
    isRecoveryMode,
    authEvent,
    sessionStatus,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    clearRecoveryMode,
    extendSession,
    refreshSession,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const auth = useAuthState();
  return createElement(AuthContext.Provider, { value: auth }, children);
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
